import { Controller } from "@hotwired/stimulus"

// Collapsible desktop sidebar rail. Toggles the collapsed class on the sidebar
// root and persists the state in a cookie so the server can render the collapsed
// class into the markup on every request (full load and Turbo navigation),
// avoiding a flash of the expanded sidebar. Only ever applied to the desktop
// sidebar (mobile is untouched).
//
// While collapsed the rail is scrollable (so it never overflows onto the sticky
// bottom section), which means a CSS-only hover tooltip would be clipped. Instead
// the hover label is rendered here as a single reusable `position: fixed` pill,
// anchored to the hovered row in viewport coordinates.
export default class extends Controller {
  static targets = ["toggle"]
  static classes = ["collapsed"]

  connect() {
    // The server already renders the `sidebar-collapsed` class from the cookie,
    // so this is idempotent — it only matters as a fallback if cookies are
    // unavailable (and keeps the toggle's aria-pressed in sync).
    this.applyState(this.readStoredState())

    this.showTooltip = this.showTooltip.bind(this)
    this.hideTooltip = this.hideTooltip.bind(this)
    this.element.addEventListener("mouseover", this.showTooltip)
    this.element.addEventListener("mouseout", this.hideTooltip)
    // Tooltip and flyout live in <body>, outside this element: Turbo's snapshot would keep a
    // visible one, and on Back it would come back with no controller to ever hide it.
    this.hideOverlays = this.hideOverlays.bind(this)
    document.addEventListener("turbo:before-cache", this.hideOverlays)
  }

  disconnect() {
    this.element.removeEventListener("mouseover", this.showTooltip)
    this.element.removeEventListener("mouseout", this.hideTooltip)
    document.removeEventListener("turbo:before-cache", this.hideOverlays)
    this.removeTooltipElement()
    this.removeFlyoutElement()
  }

  toggle() {
    const collapsed = !this.element.classList.contains(this.collapsedClass)
    this.applyState(collapsed)
    this.writeStoredState(collapsed)
    if (!collapsed) {
      this.hideTooltip()
      this.hideFlyout()
    }
  }

  applyState(collapsed) {
    this.element.classList.toggle(this.collapsedClass, collapsed)
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-pressed", String(collapsed))
    }
  }

  hideOverlays() {
    this.hideTooltip()
    this.hideFlyout()
  }

  // --- Hover tooltip (collapsed mode only) ---------------------------------

  showTooltip(event) {
    if (!this.collapsed) return
    const link = event.target.closest("a")
    if (!link || !this.element.contains(link)) return

    // An entry with sub-pages (Sidebar::ItemComponent) gets a flyout listing them instead of
    // the label pill: the rail has no room for its accordion.
    const group = link.closest(".sidebar-item-group")
    if (group && !link.classList.contains("sidebar-subpage")) {
      this.hideTooltip()
      this.showFlyout(group, link)
      return
    }
    this.hideFlyout()

    const label = this.labelFor(link)
    if (!label) return

    const tooltip = this.tooltipElement
    tooltip.textContent = label

    const rect = link.getBoundingClientRect()
    tooltip.style.left = `${rect.right + 8}px`
    tooltip.style.top = `${rect.top + rect.height / 2}px`
    tooltip.style.transform = "translateY(-50%)"
    tooltip.style.display = "block"
  }

  hideTooltip(event) {
    if (this.tooltip) this.tooltip.style.display = "none"
    // Leaving the entry towards its flyout keeps the flyout; anything else closes it shortly,
    // so the pointer can cross the gap between rail and flyout.
    if (event && this.flyout?.contains(event.relatedTarget)) return
    if (this.flyout?.style.display === "block") this.scheduleFlyoutHide()
  }

  // --- Sub-page flyout (collapsed mode only) -------------------------------

  showFlyout(group, link) {
    clearTimeout(this.flyoutTimer)
    const flyout = this.flyoutElement
    const title = document.createElement("div")
    title.className = "sidebar-flyout-title"
    title.textContent = this.labelFor(link)
    const pages = [...group.querySelectorAll("a.sidebar-subpage")].map((page) => {
      const copy = document.createElement("a")
      copy.href = page.getAttribute("href")
      copy.textContent = page.textContent.trim()
      if (page.getAttribute("aria-current")) copy.setAttribute("aria-current", "page")
      return copy
    })
    flyout.replaceChildren(title, ...pages)

    const rect = link.getBoundingClientRect()
    flyout.style.left = `${rect.right + 8}px`
    flyout.style.display = "block"
    const top = Math.min(rect.top - 6, window.innerHeight - flyout.offsetHeight - 8)
    flyout.style.top = `${Math.max(8, top)}px`
  }

  scheduleFlyoutHide() {
    clearTimeout(this.flyoutTimer)
    this.flyoutTimer = setTimeout(() => this.hideFlyout(), 200)
  }

  hideFlyout() {
    clearTimeout(this.flyoutTimer)
    if (this.flyout) this.flyout.style.display = "none"
  }

  get flyoutElement() {
    if (!this.flyout) {
      this.flyout = document.createElement("div")
      this.flyout.className = "sidebar-flyout"
      this.flyout.style.display = "none"
      this.flyout.addEventListener("mouseenter", () => clearTimeout(this.flyoutTimer))
      this.flyout.addEventListener("mouseleave", () => this.scheduleFlyoutHide())
      this.flyout.addEventListener("click", (event) => {
        if (event.target.closest("a")) this.hideFlyout()
      })
      document.body.appendChild(this.flyout)
    }
    return this.flyout
  }

  removeFlyoutElement() {
    clearTimeout(this.flyoutTimer)
    this.flyout?.remove()
    this.flyout = null
  }

  // The hidden label is a plain span on nav links and a bare text node on the
  // bottom-section action links — fall back to the link's own text content.
  labelFor(link) {
    const span = link.querySelector("span:not(.nav-badge):not([aria-hidden])")
    const text = span ? span.textContent : link.textContent
    return text ? text.trim() : ""
  }

  get tooltipElement() {
    if (!this.tooltip) {
      this.tooltip = document.createElement("div")
      this.tooltip.className = "sidebar-tooltip"
      this.tooltip.style.display = "none"
      document.body.appendChild(this.tooltip)
    }
    return this.tooltip
  }

  removeTooltipElement() {
    if (this.tooltip) {
      this.tooltip.remove()
      this.tooltip = null
    }
  }

  get collapsed() {
    return this.element.classList.contains(this.collapsedClass)
  }

  readStoredState() {
    return document.cookie.split("; ").some((entry) => entry === `${this.cookieName}=1`)
  }

  writeStoredState(collapsed) {
    if (collapsed) {
      document.cookie = `${this.cookieName}=1; path=/; max-age=31536000; SameSite=Lax`
    } else {
      document.cookie = `${this.cookieName}=; path=/; max-age=0; SameSite=Lax`
    }
  }

  get cookieName() {
    return "sidebar_collapsed"
  }
}
